/**
 * Project cards component for home page and projects listing page.
 *
 * @category components
 */

import { useMemo } from "react";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useUser } from "_/data/users";
import { groupById } from "_/utils";

import { Skeleton } from "_/components/skeleton";
import { UserAvatar } from "_/components/avatar";
import { RelativeDateTime } from "_/components/date-time";
import { Icon } from "_/components/icon";
import { ProjectThumbnail } from "_/components/project-thumbnail";
import { Tooltip } from "_/components/tooltip";
import { H5 } from "_/components/typography";

import {
  Attempt,
  Project,
  sortAttempts,
  useAllAttempts,
  useProjects,
} from "_/data/projects";

import * as S from "./styled";
import { projectUrls } from "_/routes";
import archiveTooltip from "_/assets/img/archiveTooltip.png";
import { Uuid } from "_/types";

dayjs.extend(relativeTime);

const EmptyProjectsContainer = () => (
  <S.EmptyProjectsContainer>
    <Icon variant="Folder" />
    {t`common.no-projects`}
  </S.EmptyProjectsContainer>
);

export const ArchivedProjectsHeader = () => (
  <S.ArchivedHeader>
    <H5>{t`common.archived`}</H5>
    <Tooltip
      placement="rightStart"
      delay={100}
      target={<Icon variant="Info" />}
      content={
        <div>
          {t`components.project-cards.archive-tooltip`}
          <img src={archiveTooltip} />
        </div>
      }
    />
  </S.ArchivedHeader>
);

const ProjectCard = ({
  project,
  attempts,
}: {
  project: Project;
  attempts: Attempt[];
}) => {
  const projectURI = projectUrls.index(project.id);
  const { data: user } = useUser({ id: project.ownerId });

  const lastAttempt = attempts.toSorted(sortAttempts)[0];

  return (
    <S.ProjectCard to={projectURI}>
      <S.FirstRowContainer>
        <S.ProjectName>{project.name}</S.ProjectName>
        {user ? (
          <div title={user?.name}>
            <UserAvatar resource={user} size="small" />
          </div>
        ) : null}
      </S.FirstRowContainer>
      <S.SecondRowContainer>
        <ProjectThumbnail project={project} />
        <S.CardMetaContainer>
          <S.CardMeta>
            <label>{t`common.modified-label-only`}</label>
            <div>
              <RelativeDateTime value={project.updatedAt} />
            </div>
          </S.CardMeta>
          <S.CardMeta>
            <label>{t`common.printed`}</label>
            <div>
              <RelativeDateTime
                value={lastAttempt?.startedAt}
                defaultValue={t`common.not-printed`}
              />
            </div>
          </S.CardMeta>
        </S.CardMetaContainer>
      </S.SecondRowContainer>
    </S.ProjectCard>
  );
};

/**
 * Load all projects and their print attempts, split into active and archived
 * lists. Shared by the card and table layouts of the projects listing.
 */
export const useProjectsListing = ({ limit }: { limit: number }) => {
  const { data: allAttempts = [], isLoading: attemptsLoading } =
    useAllAttempts();
  const { data: allProjects = [], isLoading: projectsLoading } = useProjects();

  const attemptsByRevision = useMemo(
    () => groupById(allAttempts, "revisionId"),
    [allAttempts]
  );

  const attemptsByProject = useMemo(() => {
    const m = new Map<Uuid, Attempt[]>();

    for (const p of allProjects) {
      const revisionIds = p.revisions.map((r) => r.id);
      const attempts = revisionIds.flatMap(
        (rid: Uuid) => attemptsByRevision[rid] ?? []
      );
      m.set(p.id, attempts);
    }
    return m;
  }, [allProjects, attemptsByRevision]);

  // Weight limit: 2/3 for active, 1/3 for archived (rounded up for active)
  // Temporary until we add pagination
  const splitLimit = (limitInner: number) => {
    const activeQuota = Math.ceil((limitInner * 2) / 3);
    const archivedQuota = Math.max(0, limitInner - activeQuota);
    return { activeQuota, archivedQuota };
  };
  const { active, archived } = useMemo(() => {
    const projects = [...allProjects].slice(0, limit);
    const activeAll: Project[] = [];
    const archivedAll: Project[] = [];

    for (const p of projects) {
      (p.status === "archived" ? archivedAll : activeAll).push(p);
    }

    // Apply weighted limits with rebalancing
    const { activeQuota, archivedQuota } = splitLimit(limit);

    const activeTake = Math.min(activeAll.length, activeQuota);
    const archivedTake = Math.min(archivedAll.length, archivedQuota);

    // Reallocate any unused quota
    const unusedActive = activeQuota - activeTake;
    const unusedArchived = archivedQuota - archivedTake;

    const finalActive = activeAll.slice(
      0,
      activeTake + Math.max(0, unusedArchived)
    );
    const finalArchived = archivedAll.slice(
      0,
      archivedTake + Math.max(0, unusedActive)
    );

    return { active: finalActive, archived: finalArchived };
  }, [allProjects, limit]);

  const isLoading = projectsLoading || attemptsLoading;

  return { allProjects, active, archived, attemptsByProject, isLoading };
};

export const ProjectCards = ({
  limit = 200,
  showArchived = false,
  layout = "plain",
}: {
  limit?: number;
  showArchived?: boolean;
  layout?: "grid" | "plain";
}) => {
  const { allProjects, active, archived, attemptsByProject, isLoading } =
    useProjectsListing({ limit });

  const renderCards = (list: Project[]) =>
    list.map((p) => (
      <ProjectCard
        key={p.id}
        project={p}
        attempts={attemptsByProject.get(p.id) ?? []}
      />
    ));

  const renderSkeletons = () =>
    Array.from({ length: 5 }, (_, i) => (
      <Skeleton key={i} variant="block" style={{ height: "188px" }} />
    ));

  const renderList = (list: Project[]) => {
    const content = (() => {
      if (isLoading) return renderSkeletons();
      if (list.length > 0) return renderCards(list);
      return <EmptyProjectsContainer />;
    })();

    // Use plain layout when the parent already controls sizing/grid (e.g., home dashboard carousel)
    // to avoid nesting grids and clipping cards to a single column.
    return layout === "grid" ? (
      <S.ProjectCards>{content}</S.ProjectCards>
    ) : (
      <>{content}</>
    );
  };

  return (
    <>
      {renderList(active)}

      {showArchived && allProjects.length > 0 && (
        <>
          <ArchivedProjectsHeader />
          {renderList(archived)}
        </>
      )}
    </>
  );
};
